import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import styles from './styles.module.scss';

// Types
import { FormData, ConsentState, SingleAnswerSlide, FormInputSlide, ProductType } from './types';

// Hooks
import { useSliderNavigation, useZipCodeCheck } from './hooks';

// Config - slidesConfig provides slide definitions

// Components
import ProgressBar from './components/ProgressBar';
import ChoiceGrid from './components/ChoiceGrid';
import FormInput from './components/FormInput';
import SuccessSlide from './components/SuccessSlide';
import {
  DEFAULT_OPTIONAL_CONSENTS,
  OptionalConsentAnswers,
} from '../../shared/consent/OptionalConsents';

// Utils
import { submitLead } from './utils/api';
import {
  trackSlideAnswer,
  trackSliderSuccess,
  trackSliderFailure,
  trackLeadCreated,
  trackEnpalSourceCookie,
} from './utils/tracking';
import {
  getUTMParams,
  splitFullName,
  getConsumoFromUtmTerm,
  getClientNameFromUtmContent,
} from './utils/helpers';

/**
 * SliderSwitcho2 - Copia dello slider Engie per variante Switcho 2
 */
const SliderSwitcho2: React.FC = () => {
  // Form data state
  const [formData, setFormData] = useState<FormData>({});
  const [formValues, setFormValues] = useState<Record<string, string>>({});

  // Lead state
  const [mongoId, setMongoId] = useState<string>('');

  const [optionalConsents, setOptionalConsents] = useState<OptionalConsentAnswers>(
    DEFAULT_OPTIONAL_CONSENTS
  );

  const consentForApi: ConsentState = useMemo(
    () => ({
      enpalMarketingConsent: optionalConsents.enpalMarketingConsent === true,
      thirdPartyMarketing: optionalConsents.thirdPartyMarketing === true,
      thirdPartyProfiling: optionalConsents.thirdPartyProfiling === true,
    }),
    [optionalConsents]
  );

  const [consumoAnnuo, setConsumoAnnuo] = useState<number | null>(null);
  const [clientName, setClientName] = useState<string | null>(null);

  // Navigation hook
  const {
    currentSlide,
    progress,
    goNext,
    goPrev,
    goToSlideById,
    canGoPrev,
  } = useSliderNavigation(formData);

  // ZIP check hook
  const { checkZip, zipFlag } = useZipCodeCheck();

  // Track initial source; consumo da "luce", nome da "nome" (stesse regole di slider_switcho)
  useEffect(() => {
    const utmParams = getUTMParams();
    trackEnpalSourceCookie(utmParams.utm_source || 'organic');

    const consumo = getConsumoFromUtmTerm();
    if (consumo) {
      setConsumoAnnuo(consumo);
    }

    const nome = getClientNameFromUtmContent();
    if (nome) {
      setClientName(nome);
    }
  }, []);

  // Update form data with a new value
  const updateFormData = useCallback((key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Handle single answer selection
  const handleAnswerSelect = useCallback(
    (slide: SingleAnswerSlide, value: string) => {
      updateFormData(slide.apiFieldName, value);
      trackSlideAnswer(slide.id, slide.title || '', value);

      if (slide.autoAdvance) {
        setTimeout(() => goNext(), 200);
      }
    },
    [updateFormData, goNext]
  );

  // Handle form input change
  const handleFormInputChange = useCallback(
    (name: string, value: string) => {
      setFormValues((prev) => ({ ...prev, [name]: value }));

      // Special handling for ZIP code
      if (name === 'zip') {
        checkZip(value);
        // Also update formData immediately for FVG check
        updateFormData('zip', value);
      }
    },
    [checkZip, updateFormData]
  );

  // Submit lead to API
  const handleLeadSubmit = useCallback(async () => {
    goToSlideById('loading');

    const uid = uuidv4();

    // Merge form values with form data
    const finalFormData: FormData = {
      ...formData,
      ...formValues,
    };

    try {
      const id = await submitLead(finalFormData, uid, consentForApi);

      if (id) {
        setMongoId(id);
        trackSliderSuccess();

        // Track lead with hashed email
        if (finalFormData.email) {
          const { firstName, lastName } = splitFullName(finalFormData.name || '');
          setTimeout(() => {
            trackLeadCreated(
              finalFormData.email!,
              finalFormData.phone || '',
              finalFormData.zip || '',
              firstName,
              lastName
            );
          }, 120);
        }

        goToSlideById('success');
      } else {
        trackSliderFailure();
        goToSlideById('error');
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      trackSliderFailure();
      goToSlideById('error');
    }
  }, [formData, formValues, consentForApi, goToSlideById]);

  // Handle form submission for input slides
  const handleFormSubmit = useCallback(
    async (slide: FormInputSlide) => {
      // Copy form values to form data
      Object.entries(formValues).forEach(([key, value]) => {
        updateFormData(key, value);
      });

      trackSlideAnswer(slide.id, slide.title || '', 'continue_button');

      // If this is the phone form, submit the lead
      if (slide.id === 'phone_form') {
        await handleLeadSubmit();
      } else {
        goNext();
      }
    },
    [formValues, updateFormData, goNext, handleLeadSubmit]
  );


  // Handle error retry
  const handleRetry = useCallback(() => {
    goToSlideById('location_form');
  }, [goToSlideById]);

  // Render current slide content
  const renderSlideContent = () => {
    if (!currentSlide) return null;

    switch (currentSlide.type) {
      case 'SINGLE_ANSWER': {
        const slide = currentSlide as SingleAnswerSlide;

        return (
          <>
            <div className={styles.slideBody}>
              <h3 className={styles.wizardTitle}>
                {slide.id === 'house_type' && clientName
                  ? `${clientName}, per quale tipo di edificio vuoi ridurre i costi energetici?`
                  : slide.title}
              </h3>

              <ChoiceGrid
                answers={slide.answers}
                selectedValue={formData[slide.apiFieldName]}
                onSelect={(value) => handleAnswerSelect(slide, value)}
                columns={slide.id === 'install_timeline' ? 2 : 3}
                compactInlineOnNarrow={
                  slide.id === 'customer_type' || slide.id === 'house_type'
                }
              />

              {(canGoPrev || !slide.autoAdvance) && (
                <div className={styles.buttonGroup}>
                  {canGoPrev && (
                    <button type="button" className={styles.btnPrev} onClick={goPrev}>
                      Indietro
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.btnNext}
                    onClick={goNext}
                    disabled={!formData[slide.apiFieldName]}
                  >
                    Avanti
                  </button>
                </div>
              )}
            </div>
          </>
        );
      }

      case 'FORM_INPUT': {
        const slide = currentSlide as FormInputSlide;
        const isPhoneSlide = slide.id === 'phone_form';
        const locationTitle =
          slide.id === 'location_form' && clientName
            ? `${clientName}, dove si trova l'immobile in cui installeresti?`
            : slide.title;

        return (
          <>
            <div className={styles.slideBody}>
              {locationTitle && <h3 className={styles.wizardTitle}>{locationTitle}</h3>}

              <FormInput
                inputs={slide.inputs}
                values={formValues}
                onChange={handleFormInputChange}
                onSubmit={() => handleFormSubmit(slide)}
                submitLabel={isPhoneSlide ? 'Scopri il risparmio totale' : 'Avanti'}
                showBackButton={canGoPrev}
                onBack={goPrev}
                isPhoneSlide={isPhoneSlide}
                optionalConsents={isPhoneSlide ? optionalConsents : undefined}
                onOptionalConsentsChange={
                  isPhoneSlide ? setOptionalConsents : undefined
                }
              />
            </div>
          </>
        );
      }

      case 'INFO': {
        if (currentSlide.id === 'loading') {
          return (
            <div className={styles.slideBody}>
              <h3 className={styles.wizardTitle}>{currentSlide.title}</h3>
              <p>Attendere prego...</p>
              <img 
                src="https://i.gifer.com/origin/34/34338d26023e5515f6cc8969aa027bca.gif" 
                alt="Loading..." 
                style={{ marginTop: '1rem', maxWidth: '80px', borderRadius: '10px' }}
              />
            </div>
          );
        }

        if (currentSlide.id === 'success') {
          return (
            <SuccessSlide
              productType={(formData.product_type || 'Photovoltaic') as ProductType}
              zipFlag={zipFlag}
              zipCode={formData.zip}
              mongoId={mongoId}
              phone={formData.phone}
              consumoAnnuo={consumoAnnuo}
            />
          );
        }

        if (currentSlide.id === 'error') {
          return (
            <div className={styles.slideBody}>
              <h3 className={styles.wizardTitle}>{currentSlide.title}</h3>
              <p>Si è verificato un errore...</p>
              <button
                type="button"
                className={styles.btnNext}
                onClick={handleRetry}
                style={{ marginTop: '1rem', maxWidth: '200px' }}
              >
                Riprova
              </button>
            </div>
          );
        }

        return (
          <div className={styles.slideBody}>
            {currentSlide.title && <h3 className={styles.wizardTitle}>{currentSlide.title}</h3>}
            {currentSlide.html && (
              <div dangerouslySetInnerHTML={{ __html: currentSlide.html }} />
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <section className={styles.sliderSection}>
      <div className={styles.sliderContainer}>
        <div className={styles.sliderWrapper}>
          <ProgressBar progress={progress} />

          <div className={styles.wizSlide}>
            {renderSlideContent()}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SliderSwitcho2;
